/**
 * api.ts
 * Typed HTTP client for the RIGX Operador PWA — hits the SAME kernel backend
 * as apps/frontend, via the SAME auth mechanism (httpOnly `suite_session`
 * cookie, credentials:'include'). This file is a deliberately small subset of
 * apps/frontend/src/lib/api.ts — only the endpoints this app actually calls
 * (auth + GET /rrhh/persons/me + fallas de flota, v1's scope) — not a fork of
 * the whole client. Add endpoints here as real screens land; do not pre-build
 * the rest speculatively (see proposal.md — IA is a draft).
 *
 * Response shapes are inferred from @suite/contracts-kernel /
 * @suite/contracts-rrhh / @suite/contracts-servicios so this app and the
 * backend agree on the wire format by construction.
 */
import type {
  AuthMeResponse,
  AuthSessionResponse,
  LoginBody,
  LoginResponse,
  SelectCompanyBody,
  ErrorEnvelope,
  PaginatedResponse,
} from '@suite/contracts-kernel';
import type {
  PersonWithEmploymentsResponse,
  SelfEmployeeDocumentResponse,
  ReportDocumentOfflineStatus,
  OrgChartRowResponse,
  SelfCalendarResponse,
  SelfAttendanceEventTypeResponse,
  RequestAbsence,
  AttendanceEventResponse,
  SelfEppDeliveryResponse,
  EppSign,
} from '@suite/contracts-rrhh';
import type {
  AssetResponse,
  FleetFailureResponse,
  FleetFailureDetailResponse,
  FleetFailureTransition,
  FleetFailureReportResponse,
  FleetFailureReportCreate,
  FleetFailureCreate,
  VehicleTripResponse,
  VehicleTripDetailResponse,
  VehicleTripCreate,
  FuelLoadResponse,
  FuelLoadCreate,
  FleetQrResponse,
  ChecklistSubmit,
  ChecklistInstanceResponse,
} from '@suite/contracts-servicios';
import { API_BASE, ApiError, noticeStatus, request } from './http.js';
import { saveBlob } from './save-blob.js';

// Re-exported so every existing `import { ApiError } from '../lib/api.js'`
// call site (locx-voz-multi-intencion, Phase 9) keeps working unchanged —
// see http.ts's header comment for why this moved.
export { ApiError };

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

/**
 * POST /auth/login — returns a discriminated union:
 *   { kind: 'token' }           → single-membership: session cookie is set, done.
 *   { kind: 'choose-company' }  → multi-membership: show a company picker.
 */
export function login(credentials: LoginBody): Promise<LoginResponse> {
  return request<LoginResponse>('/auth/login', { method: 'POST', body: credentials });
}

/**
 * POST /auth/select-company — exchanges the short-lived account-session token
 * (from a choose-company response) + companyId for a tenant session (cookie).
 */
export function selectCompany(
  body: SelectCompanyBody,
  accountSession: string,
): Promise<{ token: string }> {
  return request<{ token: string }>('/auth/select-company', {
    method: 'POST',
    body,
    bearer: accountSession,
  });
}

/** GET /auth/session — the app's identity source: verified claims from the cookie. */
export function getSession(): Promise<AuthSessionResponse> {
  return request<AuthSessionResponse>('/auth/session');
}

/** GET /auth/me — company + user + memberships for the authenticated tenant user. */
export function getMe(): Promise<AuthMeResponse> {
  return request<AuthMeResponse>('/auth/me');
}

/** POST /auth/logout — clears the httpOnly session cookie server-side. */
export function logout(): Promise<void> {
  return request<void>('/auth/logout', { method: 'POST' });
}

/**
 * GET /rrhh/persons/me — resolves the logged-in user to their own RRHH legajo
 * (add-field-operator-app prerequisite, commit 076fcd2). Throws ApiError(404)
 * when the account has no Person linked yet — a normal, expected case (see
 * GetMyPersonUseCase), not a system error; callers should catch it and show a
 * friendly "not linked yet" message, not a generic error banner.
 */
export function getMyPerson(): Promise<PersonWithEmploymentsResponse> {
  return request<PersonWithEmploymentsResponse>('/rrhh/persons/me');
}

// ---------------------------------------------------------------------------
// Autoservicio del empleado (nomi-autoservicio-empleado Tier A) — moved here
// from apps/frontend (2026-09-14 architecture correction): the self-service
// portal (certificado, documentos, organigrama, mi diagrama, ausencias) is a
// field-operator-facing surface, so it belongs in this app
// (app.rigx.sofistic.io), not apps/frontend (rigx.sofistic.io, admin/aprobación
// only). Hits the SAME `/rrhh/self/*` backend routes apps/frontend used —
// the backend is agnostic of which frontend calls it, nothing there changed.
// ---------------------------------------------------------------------------

/** GET /rrhh/self/documents — minimal projection of the caller's own employee_document rows. */
export function getMyDocuments(): Promise<{ items: SelfEmployeeDocumentResponse[] }> {
  return request<{ items: SelfEmployeeDocumentResponse[] }>('/rrhh/self/documents');
}

/**
 * POST /rrhh/self/documents/offline-status — tell RRHH which document files
 * this phone holds for use with no signal (operator-document-prefetch).
 */
export function reportDocumentOfflineStatus(body: ReportDocumentOfflineStatus): Promise<void> {
  return request<void>('/rrhh/self/documents/offline-status', { method: 'POST', body });
}

/**
 * Raw blob download of a PDF the backend renders (certificado laboral,
 * constancia de EPP). NOT the shared request<T>() helper — same posture as
 * uploadFile()'s own fetch below. The error envelope's code/message are
 * surfaced when the body is JSON, otherwise the given fallbacks.
 */
async function downloadPdf(path: string, filename: string, fallbackCode: string, fallbackMessage: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }
  if (!res.ok) {
    noticeStatus(res.status, path);
    let code = fallbackCode;
    let message = fallbackMessage;
    try {
      const envelope = (await res.json()) as Partial<ErrorEnvelope>;
      if (typeof envelope.code === 'string') code = envelope.code;
      if (typeof envelope.message === 'string') message = envelope.message;
    } catch {
      // Body was not JSON — keep the generic message.
    }
    throw new ApiError(res.status, code, message);
  }

  saveBlob(await res.blob(), filename);
}

/** GET /rrhh/self/employment-certificate.pdf — downloads the "Certificado Laboral" PDF. */
export function downloadEmploymentCertificatePdf(employeeNumber: string): Promise<void> {
  return downloadPdf(
    '/rrhh/self/employment-certificate.pdf',
    `certificado-laboral-${employeeNumber}.pdf`,
    'CERTIFICATE_DOWNLOAD_FAILED',
    'No se pudo generar el certificado laboral.',
  );
}

/** GET /rrhh/self/epp — my EPP deliveries, pending signature first (nomi-epp-firma S2). */
export function getMyEpp(): Promise<{ items: SelfEppDeliveryResponse[] }> {
  return request<{ items: SelfEppDeliveryResponse[] }>('/rrhh/self/epp');
}

/**
 * POST /rrhh/self/epp-deliveries/:id/sign — signs one of my pending deliveries
 * with the drawn signature (a PNG data URL). 409 EPP_DELIVERY_NOT_PENDING when
 * it was already signed.
 */
export function signMyEppDelivery(deliveryId: string, signaturePng: string): Promise<void> {
  const body: EppSign = { signaturePng };
  return request<void>(`/rrhh/self/epp-deliveries/${encodeURIComponent(deliveryId)}/sign`, { method: 'POST', body });
}

/** GET /rrhh/self/epp-constancia.pdf — the SRT 299/11 constancia de entrega de EPP (nomi-epp-firma S5). */
export function downloadMyEppConstanciaPdf(): Promise<void> {
  return downloadPdf(
    '/rrhh/self/epp-constancia.pdf',
    'constancia-epp.pdf',
    'EPP_CONSTANCIA_DOWNLOAD_FAILED',
    'No se pudo generar la constancia de EPP.',
  );
}

/** GET /rrhh/self/org-chart — flat list, tree built client-side (see OrganigramaView.vue). */
export function getOrgChart(): Promise<{ items: OrgChartRowResponse[] }> {
  return request<{ items: OrgChartRowResponse[] }>('/rrhh/self/org-chart');
}

/** GET /rrhh/self/calendar?from&to — merges employment_calendar_day + own attendance_event. */
export function getMyCalendar(from: string, to: string): Promise<SelfCalendarResponse> {
  return request<SelfCalendarResponse>(`/rrhh/self/calendar?from=${from}&to=${to}`);
}

/** GET /rrhh/self/absence-types — minimal picker for the "Solicitar ausencia" form. */
export function getMyAbsenceTypes(): Promise<{ items: SelfAttendanceEventTypeResponse[] }> {
  return request<{ items: SelfAttendanceEventTypeResponse[] }>('/rrhh/self/absence-types');
}

/** POST /rrhh/self/absences — the employee asks, RRHH decides (approval stays in apps/frontend). */
export function requestAbsence(body: RequestAbsence): Promise<AttendanceEventResponse> {
  return request<AttendanceEventResponse>('/rrhh/self/absences', { method: 'POST', body });
}

// ---------------------------------------------------------------------------
// Fallas de flota (add-locx-fleet-failure-reports PR1+PR1.5) — field-operator
// app v1. GET /servicios/fleet-failures with NO params already scopes to the
// caller's own assignedEmploymentId for a role without locx:fleet-failure-manage
// (operador_locx) — see fleet-failure.routes.ts. Nothing here re-implements
// that scoping client-side.
// ---------------------------------------------------------------------------

export interface FailureTypePickerItem {
  id: string;
  code: string;
  label: string;
}

export interface ExternalProviderPickerItem {
  id: string;
  name: string;
}

/**
 * GET /servicios/fleet-failures — "mis fallas" for the logged-in mechanic.
 * `sort=newest` overrides the route's own oldest-first default
 * (fleet-failure.routes.ts) — this worklist reads newest-first.
 */
export function listMyFailures(): Promise<PaginatedResponse<FleetFailureResponse>> {
  return request<PaginatedResponse<FleetFailureResponse>>('/servicios/fleet-failures?sort=newest');
}

/** GET /servicios/fleet-failures/:id — full detail + availableTransitions. */
export function getFailure(id: string): Promise<FleetFailureDetailResponse> {
  return request<FleetFailureDetailResponse>(`/servicios/fleet-failures/${id}`);
}

/** POST /servicios/fleet-failures/:id/transitions — advance the FSM. */
export function transitionFailure(id: string, body: FleetFailureTransition): Promise<FleetFailureResponse> {
  return request<FleetFailureResponse>(`/servicios/fleet-failures/${id}/transitions`, { method: 'POST', body });
}

/** GET /servicios/fleet-failure-reports/:id — the parent informe (has assetId). */
export function getReport(id: string): Promise<FleetFailureReportResponse> {
  return request<FleetFailureReportResponse>(`/servicios/fleet-failure-reports/${id}`);
}

/** POST /servicios/fleet-failure-reports — opens a new informe against an asset. */
export function createReport(body: FleetFailureReportCreate): Promise<FleetFailureReportResponse> {
  return request<FleetFailureReportResponse>('/servicios/fleet-failure-reports', { method: 'POST', body });
}

/** POST /servicios/fleet-failures — files a falla under an existing informe. */
export function createFailure(body: FleetFailureCreate): Promise<FleetFailureResponse> {
  return request<FleetFailureResponse>('/servicios/fleet-failures', { method: 'POST', body });
}

/** GET /servicios/assets — debounced search picker (elegir vehículo/equipo). */
export function searchAssets(query: string): Promise<PaginatedResponse<AssetResponse>> {
  const qs = query ? `?search=${encodeURIComponent(query)}&pageSize=20` : '?pageSize=20';
  return request<PaginatedResponse<AssetResponse>>(`/servicios/assets${qs}`);
}

/** GET /servicios/assets?page — one page of 100 units, kept for the picker with no signal. */
export function listAssetsPage(page: number): Promise<PaginatedResponse<AssetResponse>> {
  return request<PaginatedResponse<AssetResponse>>(`/servicios/assets?page=${page}&pageSize=100`);
}

/** GET /servicios/assets/:id — resolve an asset's display fields (serialNumber). */
export function getAsset(id: string): Promise<AssetResponse> {
  return request<AssetResponse>(`/servicios/assets/${id}`);
}

/** GET /servicios/order-options/failure-types — unguarded catalog picker. */
export function listFailureTypes(): Promise<{ items: FailureTypePickerItem[] }> {
  return request<{ items: FailureTypePickerItem[] }>('/servicios/order-options/failure-types');
}

/** GET /servicios/order-options/external-providers — unguarded catalog picker. */
export function listExternalProviders(): Promise<{ items: ExternalProviderPickerItem[] }> {
  return request<{ items: ExternalProviderPickerItem[] }>('/servicios/order-options/external-providers');
}

// ---------------------------------------------------------------------------
// Gerenciamiento de viaje (add-locx-trip-fuel-tracking PR2b) — field-operator
// app v1. Unlike fallas de flota, GET /servicios/vehicle-trips has NO
// server-side "mine" filter (the admin approval screen in apps/frontend needs
// to see every trip) — TripsListView.vue filters client-side by the caller's
// own requestedByEmploymentId, resolved the same way ReportarFallaView.vue
// resolves ownEmploymentId: GET /rrhh/persons/me's `activeEmploymentId`
// (nomi-autoservicio-empleado Fase 0, single active-employment criterion,
// NOMI-29). PATCH /servicios/vehicle-trips/:id/transition (approve/reject/
// no-realizado) is intentionally NOT wired here — that screen lives in
// apps/frontend (PR3), the field operator only ever creates/reads.
// ---------------------------------------------------------------------------

/** GET /servicios/vehicle-trips — "mis viajes" (client-side filtered, see above). */
export function listMyTrips(): Promise<PaginatedResponse<VehicleTripResponse>> {
  return request<PaginatedResponse<VehicleTripResponse>>('/servicios/vehicle-trips?pageSize=100');
}

/** GET /servicios/vehicle-trips/:id — full detail + availableTransitions (read-only here). */
export function getTrip(id: string): Promise<VehicleTripDetailResponse> {
  return request<VehicleTripDetailResponse>(`/servicios/vehicle-trips/${id}`);
}

/** POST /servicios/vehicle-trips — request authorization to circulate a vehicle. */
export function createTrip(body: VehicleTripCreate): Promise<VehicleTripResponse> {
  return request<VehicleTripResponse>('/servicios/vehicle-trips', { method: 'POST', body });
}

// ---------------------------------------------------------------------------
// Carga de combustible (add-locx-trip-fuel-tracking PR2b).
// ---------------------------------------------------------------------------

export interface TankerPickerItem {
  id: string;
  serialNumber: string;
  description: string | null;
}

export interface CostCenterPickerItem {
  id: string;
  name: string;
  label: string | null;
}

/**
 * One asset type from the unguarded tenant picker. `tracksHours`/`tracksKm`
 * (locx-paridad-operativa-bramix Slice B, MR-2.3/MR-6) are the SAME flags
 * `assertMeterReadings` reads server-side — the "meter present ⇒ reading
 * required" rule lives once, in the backend; the frontend only consumes the
 * flags to mark a field required, never re-derives the rule.
 */
export interface AssetTypePickerItem {
  id: string;
  code: string;
  label: string;
  tracksHours: boolean;
  tracksKm: boolean;
}

/** GET /servicios/order-options/tankers — unguarded cisterna (tanker asset) picker. */
export function listTankers(): Promise<{ items: TankerPickerItem[] }> {
  return request<{ items: TankerPickerItem[] }>('/servicios/order-options/tankers');
}

/** GET /servicios/order-options/asset-types — unguarded tenant asset-type picker. */
export function listAssetTypes(): Promise<{ items: AssetTypePickerItem[] }> {
  return request<{ items: AssetTypePickerItem[] }>('/servicios/order-options/asset-types');
}

/** GET /servicios/order-options/cost-centers — unguarded cost-center picker. */
export function listCostCenters(): Promise<{ items: CostCenterPickerItem[] }> {
  return request<{ items: CostCenterPickerItem[] }>('/servicios/order-options/cost-centers');
}

/** GET /servicios/fuel-loads — all fuel loads (see FuelLoadsListView.vue for the scoping decision). */
export function listFuelLoads(): Promise<PaginatedResponse<FuelLoadResponse>> {
  return request<PaginatedResponse<FuelLoadResponse>>('/servicios/fuel-loads?pageSize=100');
}

/** GET /servicios/fuel-loads/:id — full detail. */
export function getFuelLoad(id: string): Promise<FuelLoadResponse> {
  return request<FuelLoadResponse>(`/servicios/fuel-loads/${id}`);
}

/** POST /servicios/fuel-loads — record a fuel load from a tanker to a receiver asset. */
export function createFuelLoad(body: FuelLoadCreate): Promise<FuelLoadResponse> {
  return request<FuelLoadResponse>('/servicios/fuel-loads', { method: 'POST', body });
}

/** POST /servicios/fuel-loads/:id/signature — attach a previously-uploaded fileId as the receiver's signature. */
export function attachFuelLoadSignature(id: string, fileId: string): Promise<FuelLoadResponse> {
  return request<FuelLoadResponse>(`/servicios/fuel-loads/${id}/signature`, { method: 'POST', body: { fileId } });
}

/**
 * POST /files — generic kernel file upload (multipart/form-data, field name
 * 'file'). Bypasses the JSON `request<T>` helper (this is multipart, not
 * JSON) but reuses the same API_BASE/credentials/ApiError posture so callers
 * can't tell the difference. Used to upload the fuel-load signature PNG blob
 * captured by SignaturePad (@suite/ui) before attaching it via
 * attachFuelLoadSignature().
 */
export async function uploadFile(blob: Blob, filename: string): Promise<{ fileId: string }> {
  const formData = new FormData();
  formData.append('file', blob, filename);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/files`, { method: 'POST', body: formData, credentials: 'include' });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }

  if (!res.ok) {
    noticeStatus(res.status, '/files');
    let code = 'UNKNOWN';
    let message = `Request failed with status ${res.status}`;
    try {
      const envelope = (await res.json()) as Partial<ErrorEnvelope>;
      if (typeof envelope.code === 'string') code = envelope.code;
      if (typeof envelope.message === 'string') message = envelope.message;
    } catch {
      // Body was not JSON — keep the generic message.
    }
    throw new ApiError(res.status, code, message);
  }

  return (await res.json()) as { fileId: string };
}

// ---------------------------------------------------------------------------
// Fleet QR (fleet-qr-compliance PR3/PR4) — requires the SAME authenticated
// session as everything else in this app (require-login-for-qr, 2026-08-20).
// The token in the URL just identifies which asset within the caller's own
// company; it's no longer the auth by itself.
// ---------------------------------------------------------------------------

/** GET /servicios/fleet-qr/:token — unit documentation + checklist status. */
export function getFleetQr(token: string): Promise<FleetQrResponse> {
  return request<FleetQrResponse>(`/servicios/fleet-qr/${token}`);
}

/** POST /servicios/fleet-qr/:token/checklist — submit (or resubmit) this period's checklist. */
export function submitFleetQrChecklist(token: string, body: ChecklistSubmit): Promise<ChecklistInstanceResponse> {
  return request<ChecklistInstanceResponse>(`/servicios/fleet-qr/${token}/checklist`, { method: 'POST', body });
}

/**
 * POST /servicios/fleet-qr/:token/photo — same shape as uploadFile() but
 * hits the qr-flow-scoped endpoint. Used for a checklist item's optional
 * "falla" photo. Raw fetch() (not request()) because this is multipart, but
 * still needs credentials:'include' — same session cookie as everything else.
 */
export async function uploadFleetQrPhoto(token: string, blob: Blob, filename: string): Promise<{ fileId: string }> {
  const formData = new FormData();
  formData.append('file', blob, filename);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}/servicios/fleet-qr/${token}/photo`, { method: 'POST', body: formData, credentials: 'include' });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }

  if (!res.ok) {
    noticeStatus(res.status, `/servicios/fleet-qr/${token}/photo`);
    let code = 'UNKNOWN';
    let message = `Request failed with status ${res.status}`;
    try {
      const envelope = (await res.json()) as Partial<ErrorEnvelope>;
      if (typeof envelope.code === 'string') code = envelope.code;
      if (typeof envelope.message === 'string') message = envelope.message;
    } catch {
      // Body was not JSON — keep the generic message.
    }
    throw new ApiError(res.status, code, message);
  }

  return (await res.json()) as { fileId: string };
}
